import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Check, ArrowLeft, Shield } from 'lucide-react';
import WalletConnectModal from '../components/WalletConnectModal';
import { useAuth } from '../App';
import toast from 'react-hot-toast';
import './Auth.css';

const benefits = [
  'Bank-grade security for your assets',
  'Support for 50+ blockchain networks',
  'Real-time portfolio tracking',
  '24/7 customer support',
  'Multi-signature protection',
  'Encrypted cloud backup',
];

const GetStarted = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { registerUser } = useAuth();
  const navigate = useNavigate();

  const handleWalletConnect = async (walletData) => {
    try {
      await registerUser(walletData);
      sessionStorage.setItem('walletJustConnected', 'true');
      setIsModalOpen(false);
      toast.success('Account created successfully!');
      navigate('/backup-success');
    } catch (error) {
      console.error('Registration error:', error);
      const msg = error?.message?.includes('timed out')
        ? 'Server is waking up. Please wait a moment and try again.'
        : 'Could not create account. Please try again.';
      toast.error(msg);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Left Side - Branding */}
        <div className="auth-branding">
          <div className="branding-content">
            <Link to="/" className="auth-logo">
              <div className="logo-icon">
                <Shield size={24} />
              </div>
              <span className="logo-text">
                Web3<span className="logo-highlight">Ledger</span>
              </span>
            </Link>

            <div className="branding-text">
              <h1>Start Your<br />Secure Journey</h1>
              <p>Join millions of users who trust Web3 Ledger for their digital asset management.</p>
            </div>

            <div className="benefits-list">
              {benefits.map((benefit, index) => (
                <div key={index} className="benefit-item">
                  <div className="benefit-check">
                    <Check size={14} />
                  </div>
                  <span>{benefit}</span>
                </div>
              ))}
            </div>

            <div className="branding-footer">
              <p>Trusted by 2M+ users worldwide</p>
            </div>
          </div>

          <div className="branding-bg">
            <div className="bg-gradient"></div>
            <div className="bg-pattern"></div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="auth-form-section">
          <div className="auth-form-container">
            <Link to="/" className="back-link">
              <ArrowLeft size={18} />
              Back to Home
            </Link>

            <div className="auth-form-content">
              <div className="form-header">
                <h2>Get Started</h2>
                <p>Create your account by connecting your wallet</p>
              </div>

              <button 
                className="connect-wallet-btn"
                onClick={() => setIsModalOpen(true)}
              >
                <div className="btn-icon">
                  <Shield size={24} />
                </div>
                <div className="btn-text">
                  <span className="btn-title">Connect Wallet</span>
                  <span className="btn-subtitle">Secure connection with encryption</span>
                </div>
                <ArrowRight size={20} />
              </button>

              <div className="form-divider">
                <span>Supported Wallets</span>
              </div>

              <div className="wallet-logos">
                <div className="wallet-logo" title="MetaMask">
                  <svg viewBox="0 0 35 33" fill="none" xmlns="http://www.w3.org/2000/svg" width="32" height="32">
                    <path d="M32.96 1l-13.14 9.72 2.45-5.73L32.96 1z" fill="#E2761B" stroke="#E2761B" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2.66 1l13.02 9.8-2.33-5.81L2.66 1zM28.23 23.53l-3.5 5.34 7.49 2.06 2.14-7.28-6.13-.12zM1.27 23.65l2.13 7.28 7.47-2.06-3.48-5.34-6.12.12z" fill="#E4761B" stroke="#E4761B" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10.47 14.51l-2.08 3.14 7.4.34-.26-7.96-5.06 4.48zM25.16 14.51l-5.13-4.56-.17 8.04 7.38-.34-2.08-3.14zM10.87 28.87l4.49-2.16-3.86-3.01-.63 5.17zM20.28 26.71l4.48 2.16-.62-5.17-3.86 3.01z" fill="#E4761B" stroke="#E4761B" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M24.76 28.87l-4.48-2.16.36 2.9-.04 1.23 4.16-1.97zM10.87 28.87l4.16 1.97-.03-1.23.35-2.9-4.48 2.16z" fill="#D7C1B3" stroke="#D7C1B3" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M15.1 21.78l-3.7-1.08 2.62-1.2 1.08 2.28zM20.53 21.78l1.09-2.28 2.63 1.2-3.72 1.08z" fill="#233447" stroke="#233447" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10.87 28.87l.65-5.34-4.13.12 3.48 5.22zM24.11 23.53l.65 5.34 3.48-5.22-4.13-.12zM27.24 17.65l-7.38.34.68 3.79 1.09-2.28 2.63 1.2 2.98-3.05zM11.4 20.7l2.63-1.2 1.08 2.28.69-3.79-7.4-.34 2.99 3.05z" fill="#CD6116" stroke="#CD6116" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8.4 17.65l3.1 6.05-.1-3-2.99-3.05zM24.26 20.7l-.12 3 3.1-6.05-2.98 3.05zM15.8 17.99l-.69 3.79.87 4.5.2-5.93-.38-2.36zM19.86 17.99l-.37 2.35.18 5.94.87-4.5-.68-3.79z" fill="#E4751F" stroke="#E4751F" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M20.54 21.78l-.87 4.5.63.44 3.86-3.01.12-3-3.74 1.07zM11.4 20.7l.1 3.01 3.86 3.01.63-.44-.87-4.5-3.72-1.08z" fill="#F6851B" stroke="#F6851B" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M20.6 30.84l.04-1.23-.34-.29h-4.96l-.33.29.03 1.23-4.17-1.97 1.46 1.2 2.95 2.03h5.05l2.96-2.04 1.44-1.19-4.14 1.97z" fill="#C0AD9E" stroke="#C0AD9E" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M20.28 26.71l-.63-.44h-3.67l-.63.44-.35 2.9.33-.29h4.96l.34.29-.35-2.9z" fill="#161616" stroke="#161616" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M33.52 11.35l1.1-5.36L32.95 1l-12.67 9.4 4.88 4.11 6.89 2.01 1.52-1.77-.66-.48 1.05-.96-.8-.62 1.05-.8-.7-.53zM1 5.99l1.11 5.36-.71.53 1.06.8-.8.62 1.05.96-.66.48 1.52 1.77 6.89-2.01 4.88-4.11L2.67 1 1 5.99z" fill="#763D16" stroke="#763D16" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M32.05 16.52l-6.89-2.01 2.08 3.14-3.1 6.05 4.1-.05h6.13l-2.32-7.13zM10.47 14.51l-6.89 2.01-2.3 7.13h6.12l4.1.05-3.1-6.05 2.07-3.14zM19.86 17.99l.44-7.59 2-5.4h-8.93l2 5.4.44 7.59.16 2.37.02 5.92h3.67l.02-5.92.18-2.37z" fill="#F6851B" stroke="#F6851B" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="wallet-logo" title="Trust Wallet">
                  <img src={`${process.env.PUBLIC_URL}/wallet-icons/Trust-wallet.png`} alt="Trust Wallet" />
                </div>
                <div className="wallet-logo" title="Coinbase Wallet">
                  <svg width="32" height="32" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="20" cy="20" r="16" fill="#0052FF"/>
                    <path d="M20 10C14.48 10 10 14.48 10 20C10 25.52 14.48 30 20 30C25.52 30 30 25.52 30 20C30 14.48 25.52 10 20 10ZM17 23V17H23V23H17Z" fill="white"/>
                  </svg>
                </div>
                <div className="wallet-logo" title="Crypto.com">
                  <svg width="32" height="32" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="40" height="40" rx="8" fill="#002D74"/>
                    <path d="M20 6L8 13V27L20 34L32 27V13L20 6Z" fill="#002D74" stroke="white" strokeWidth="1.5"/>
                    <path d="M20 10L12 14.5V25.5L20 30L28 25.5V14.5L20 10Z" fill="white"/>
                    <path d="M20 14L15 17V23L20 26L25 23V17L20 14Z" fill="#002D74"/>
                  </svg>
                </div>
                <div className="wallet-logo" title="Phantom">
                  <svg width="32" height="32" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="40" height="40" rx="8" fill="url(#phantom-grad)"/>
                    <path d="M29.5 20.5C29.5 26.29 24.79 31 19 31C13.21 31 8.5 26.29 8.5 20.5C8.5 14.71 13.21 10 19 10H29.5V20.5Z" fill="white"/>
                    <circle cx="14" cy="19" r="2" fill="#AB9FF2"/>
                    <circle cx="21" cy="19" r="2" fill="#AB9FF2"/>
                    <defs><linearGradient id="phantom-grad" x1="0" y1="0" x2="40" y2="40"><stop stopColor="#534BB1"/><stop offset="1" stopColor="#551BF9"/></linearGradient></defs>
                  </svg>
                </div>
                <div className="wallet-logo" title="Ledger">
                  <svg width="32" height="32" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="40" height="40" rx="8" fill="#000"/>
                    <path d="M10 24.5V29H17.5V27.5H11.5V24.5H10ZM10 10V15.5H11.5V11.5H17.5V10H10ZM22.5 27.5V29H30V24.5H28.5V27.5H22.5ZM28.5 10V11.5H22.5V10H28.5ZM28.5 11.5H30V15.5H28.5V11.5ZM17.5 15.5V17H10V24.5H17.5V17H22.5V15.5H17.5Z" fill="white"/>
                  </svg>
                </div>
                <div className="wallet-logo" title="Binance">
                  <svg width="32" height="32" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="40" height="40" rx="8" fill="#F3BA2F"/>
                    <path d="M20 11L24.5 15.5L22.3 17.7L20 15.4L17.7 17.7L15.5 15.5L20 11Z" fill="#1E2026"/>
                    <path d="M12 18L14.2 20.2L12 22.4L9.8 20.2L12 18Z" fill="#1E2026"/>
                    <path d="M20 18L24.5 22.5L22.3 24.7L20 22.4L17.7 24.7L15.5 22.5L20 18Z" fill="#1E2026"/>
                    <path d="M28 18L30.2 20.2L28 22.4L25.8 20.2L28 18Z" fill="#1E2026"/>
                    <path d="M20 25L24.5 29.5L22.3 31.7L20 29.4L17.7 31.7L15.5 29.5L20 25Z" fill="#1E2026"/>
                  </svg>
                </div>
                <div className="wallet-logo" title="WalletConnect">
                  <svg width="32" height="32" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="40" height="40" rx="8" fill="#3B99FC"/>
                    <path d="M12.5 16C16.5 12 23.5 12 27.5 16L28 16.5C28.2 16.7 28.2 17 28 17.2L26.5 18.7C26.4 18.8 26.2 18.8 26.1 18.7L25.4 18C22.5 15.1 17.5 15.1 14.6 18L13.9 18.7C13.8 18.8 13.6 18.8 13.5 18.7L12 17.2C11.8 17 11.8 16.7 12 16.5L12.5 16ZM31 19.5L32.3 20.8C32.5 21 32.5 21.3 32.3 21.5L25.7 28.2C25.5 28.4 25.1 28.4 24.9 28.2L20.4 23.7C20.35 23.65 20.25 23.65 20.2 23.7L15.7 28.2C15.5 28.4 15.1 28.4 14.9 28.2L8.3 21.5C8.1 21.3 8.1 21 8.3 20.8L9.6 19.5C9.8 19.3 10.2 19.3 10.4 19.5L14.9 24C14.95 24.05 15.05 24.05 15.1 24L19.6 19.5C19.8 19.3 20.2 19.3 20.4 19.5L24.9 24C24.95 24.05 25.05 24.05 25.1 24L29.6 19.5C29.8 19.3 30.2 19.3 30.4 19.5L31 19.5Z" fill="white"/>
                  </svg>
                </div>
                <div className="wallet-logo more-wallets" title="More Wallets">
                  <span>+30</span>
                </div>
              </div>

              <div className="auth-footer">
                <p>
                  Already have an account?{' '}
                  <Link to="/login">Login</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <WalletConnectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConnect={handleWalletConnect}
        mode="register"
      />
    </div>
  );
};

export default GetStarted;
